import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Scrollbar from '../scrollbar';
import { TABLE_HEAD } from '../../utils/employeeConstants';

const visuallyHiddenSx = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  top: 20,
  width: '1px',
};

const EmployeeTable = ({
  employees,
  isLoading,
  page,
  rowsPerPage,
  totalCount,
  sortBy,
  sortDirection,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
  onViewEmployee,
  onEditEmployee,
  onDeleteEmployee,
  onCreateUserAccount,
}) => {
  const getStatusDisplay = (employee) => (employee.is_active ? 'Active' : 'Inactive');
  const getStatusColor = (employee) => (employee.is_active ? 'success' : 'error');

  const handleHeaderSortClick = useCallback(
    (columnId) => {
      if (!onSortChange) return;
      if (sortBy !== columnId) {
        onSortChange(columnId, 'asc');
        return;
      }
      if (sortDirection === 'asc') {
        onSortChange(columnId, 'desc');
        return;
      }
      onSortChange('', 'asc');
    },
    [sortBy, sortDirection, onSortChange]
  );

  return (
    <Card>
      <Scrollbar>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map((headCell) => {
                  const align = headCell.alignRight ? 'right' : 'left';
                  const isActive = headCell.sortable && sortBy === headCell.id;
                  return (
                    <TableCell
                      key={headCell.id}
                      align={align}
                      sortDirection={isActive ? sortDirection : false}
                      sx={{
                        whiteSpace: 'nowrap',
                        ...(headCell.sortable && {
                          '& .MuiTableSortLabel-icon': {
                            opacity: isActive ? 1 : 0.35,
                            transition: 'opacity 0.15s ease-in-out',
                          },
                          '&:hover .MuiTableSortLabel-icon': {
                            opacity: isActive ? 1 : 0.7,
                          },
                        }),
                      }}
                    >
                      {headCell.sortable ? (
                        <Tooltip
                          title={
                            isActive
                              ? sortDirection === 'asc'
                                ? 'Sorted ascending — click to sort descending'
                                : 'Sorted descending — click to clear sort'
                              : 'Click to sort ascending'
                          }
                          enterDelay={400}
                          arrow
                        >
                          <TableSortLabel
                            active={isActive}
                            direction={isActive ? sortDirection : 'asc'}
                            onClick={() => handleHeaderSortClick(headCell.id)}
                            sx={{
                              fontWeight: isActive ? 700 : 600,
                              color: isActive ? 'primary.main' : 'inherit',
                              '&.Mui-active': {
                                color: 'primary.main',
                              },
                              '&.Mui-active .MuiTableSortLabel-icon': {
                                color: 'primary.main',
                              },
                            }}
                          >
                            {headCell.label}
                            {isActive ? (
                              <Box component="span" sx={visuallyHiddenSx}>
                                {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </Tooltip>
                      ) : (
                        <Typography
                          component="span"
                          variant="inherit"
                          sx={{ fontWeight: 600, color: 'text.primary' }}
                        >
                          {headCell.label}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : employees?.length === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                    <Paper sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2">No employees found.</Typography>
                    </Paper>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {employees.map((employee) => (
                  <TableRow hover key={employee.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {employee?.full_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {employee?.employee_id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{employee.designation}</TableCell>
                    <TableCell>{employee.department}</TableCell>
                    <TableCell>{employee.shift?.name || 'N/A'}</TableCell>
                    <TableCell>{employee.email}</TableCell>
                    <TableCell>{employee.phone || 'N/A'}</TableCell>
                    <TableCell>
                      {employee.user ? (
                        <IconButton
                          size="small"
                          sx={{
                            color: 'success.main',
                            '&:hover': { backgroundColor: 'transparent' },
                            cursor: 'default',
                          }}
                          onClick={(e) => e.preventDefault()}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => onCreateUserAccount(employee)}
                          sx={{ color: 'primary.main' }}
                          title="Create User Account"
                        >
                          <PersonAddIcon />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getStatusColor(employee) === 'success' ? 'success.main' : 'error.main',
                          fontWeight: 500,
                        }}
                      >
                        {getStatusDisplay(employee)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Open Employee Dashboard">
                          <span>
                            <IconButton
                              size="small"
                              component={RouterLink}
                              to={`/hr/employee-dashboard?employee_id=${employee.id}`}
                              sx={{ color: 'info.main' }}
                            >
                              <DashboardIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => onViewEmployee(employee)}
                          sx={{ color: 'primary.main' }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onEditEmployee(employee)}
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onDeleteEmployee(employee)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Scrollbar>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Card>
  );
};

EmployeeTable.propTypes = {
  employees: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  onSortChange: PropTypes.func,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onViewEmployee: PropTypes.func.isRequired,
  onEditEmployee: PropTypes.func.isRequired,
  onDeleteEmployee: PropTypes.func.isRequired,
  onCreateUserAccount: PropTypes.func.isRequired,
};

EmployeeTable.defaultProps = {
  employees: [],
  isLoading: false,
  sortBy: '',
  sortDirection: 'asc',
  onSortChange: undefined,
};

export default EmployeeTable;
